import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { blogPosts } from '../data/blog';

const BLOG_IMAGES = {
  'Web Development': 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&q=80',
  'SEO': 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=1200&q=80',
  'App Development': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&q=80',
  'Digital Marketing': 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=1200&q=80',
  'Web Design': 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1200&q=80',
};

export default function BlogDetail() {
  useScrollReveal();
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = blogPosts.filter(p => p.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO title={post.title} description={post.excerpt} canonical={`/blog/${post.slug}`} ogType="article" />

      {/* Back */}
      <div className="bg-pure-black  border-white/10 pt-16">
        <div className="container-max py-4">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors font-medium">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Hero image */}
      <div className="bg-pure-black pb-16">
        <div className="container-max">
          <div className="overflow-hidden rounded-2xl h-64 md:h-96">
            <img
              src={BLOG_IMAGES[post.category] || BLOG_IMAGES['Web Development']}
              alt={post.title}
              className="w-full h-full object-cover opacity-80"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-grid-black pb-16 pt-10">
        <div className="container-max max-w-3xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="text-xs font-bold text-white bg-white/10 border border-white/10 px-3 py-1.5 rounded-full">{post.category}</span>
            <span className="text-xs text-white/30">{post.readTime}</span>
            <span className="text-xs text-white/30">{post.date}</span>
          </div>
          <h1 className="text-3xl md:text-5xl text-white leading-tight mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{post.title}</h1>
          <p className="text-lg text-white/50 leading-relaxed mb-8">{post.excerpt}</p>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center text-black font-black text-sm">CV</div>
            <div>
              <p className="text-sm font-bold text-white">{post.author.name}</p>
              <p className="text-xs text-white/30">{post.author.role}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <article className="bg-grid-white py-14 md:py-20">
        <div className="container-max max-w-3xl">
          <div className="prose-custom reveal" dangerouslySetInnerHTML={{ __html: post.content }} />

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-12 pt-10 border-t border-black/10 reveal">
            {post.tags.map(tag => (
              <span key={tag} className="text-xs font-medium text-black/50 bg-black/5 border border-black/10 px-3 py-1.5 rounded-full">#{tag}</span>
            ))}
          </div>

          {/* CTA inside blog */}
          <div className="mt-12 bg-black rounded-2xl p-8 md:p-10 text-center reveal">
            <h3 className="text-2xl text-white mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>NEED HELP WITH YOUR PROJECT?</h3>
            <p className="text-white/50 text-sm mb-5">Talk to our team. Free consultation, no commitment.</p>
            <Link to="/contact" className="btn-primary-inv inline-flex">
              Get Free Quote →
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="bg-grid-black py-16 border-t border-white/10">
        <div className="container-max">
          <h2 className="text-2xl text-white mb-8 reveal" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>MORE FROM THE BLOG</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map(({ slug: rslug, title, category, readTime, date }, i) => (
              <Link key={rslug} to={`/blog/${rslug}`} className="reveal card-black group overflow-hidden flex flex-col" style={{transitionDelay:`${i*60}ms`}}>
                <div className="h-36 overflow-hidden rounded-t-2xl">
                  <img src={BLOG_IMAGES[category] || BLOG_IMAGES['Web Development']} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-70" loading="lazy" />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-white/50">{category}</span>
                    <span className="text-xs text-white/20">{readTime}</span>
                  </div>
                  <h3 className="text-white text-sm leading-snug group-hover:text-white/60 transition-colors flex-1" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                  <p className="text-xs text-white/20 mt-4">{date}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
